import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import {
  selectedUnifiedIdAtom,
  selectedVisitAtom,
} from '@/app/appHeader/imageViewerMenu/atoms';
import { showPastVisitsAtom } from '@/modules/annotator/atoms';
import { useGetCurrentImage } from '@/modules/annotator/multiFileViewer/findingViewer/imageBrowser/helpers';
import { compareImagesAtom } from '@/modules/annotator/multiFileViewer/imageToolbar/atoms';
import type { ImageObject } from '@/modules/annotator/types';
import type { TVisitSummary } from '@/react-query/useVisitSummaries';
import { useVisitSummaries } from '@/react-query/useVisitSummaries';

export const getImagesForVisit = (
  images: ImageObject[],
  visitId: number | undefined | null,
) => {
  return images.filter((image) => image.estimatedVisitId === visitId);
};

// Simple utility to convert LatestImage to ImageObject following existing patterns
export const convertLatestImageToImageObject = (
  latestImage: any,
  visitId?: number,
): ImageObject => {
  return {
    ...latestImage,
    objectId: latestImage.imageId,
    estimatedVisitId: visitId,
  };
};

export type VisitIdToImagesMap = {
  estimatedVisitId?: number;
  unifiedAppointmentId?: number;
  images: ImageObject[];
}[];

export type VisitIdToImageIdsMap = {
  estimatedVisitId?: number;
  unifiedAppointmentId?: number;
  imageIds: string[];
}[];

// Helper function to format visit summary data for MixPanel
export const formatMixPanelData = (visitSummaryData: TVisitSummary[]) => {
  return visitSummaryData.length > 1
    ? visitSummaryData.reduce(
        (acc, summary, index) => ({
          ...acc,
          [`estimatedVisitId${index + 1}`]: summary.estimatedVisitId,
          [`unifiedAppointmentId${index + 1}`]: summary.unifiedAppointmentId,
        }),
        {},
      )
    : {
        estimatedVisitId: visitSummaryData.at(-1)?.estimatedVisitId,
        unifiedAppointmentId: visitSummaryData.at(-1)?.unifiedAppointmentId,
      };
};

// hook to get visit summaries and images for preview dialog
export const usePatientPrintoutData = () => {
  const selectedVisitId = useAtomValue(selectedVisitAtom);
  const selectedUnifiedId = useAtomValue(selectedUnifiedIdAtom);
  const compareImages = useAtomValue(compareImagesAtom);
  const showPastVisits = useAtomValue(showPastVisitsAtom);
  const selectedImages = useGetCurrentImage();
  const currentImage = selectedImages?.currentImage;

  const isCompareMode =
    !!compareImages?.leftImage &&
    !!compareImages?.rightImage &&
    !!compareImages?.leftVisitId &&
    !!compareImages?.rightVisitId &&
    showPastVisits;

  const visitIdToImagesMap: VisitIdToImagesMap = useMemo(() => {
    if (isCompareMode) {
      const images = [
        convertLatestImageToImageObject(
          compareImages.leftImage,
          compareImages.leftVisitId,
        ),
        convertLatestImageToImageObject(
          compareImages.rightImage,
          compareImages.rightVisitId,
        ),
      ];

      const result = [
        {
          estimatedVisitId: Number(compareImages.leftVisitId),
          images: [images[0]],
        },
        {
          estimatedVisitId: Number(compareImages.rightVisitId),
          images: [images[1]],
        },
      ];

      return result;
    }

    if (selectedVisitId && selectedUnifiedId && currentImage) {
      const result = [
        {
          estimatedVisitId: Number(selectedVisitId),
          unifiedAppointmentId: Number(selectedUnifiedId),
          images: [currentImage],
        },
      ];
      return result;
    }

    if (selectedVisitId && currentImage) {
      const result = [
        {
          estimatedVisitId: Number(selectedVisitId),
          images: [currentImage],
        },
      ];
      return result;
    }

    return [];
  }, [
    compareImages,
    selectedVisitId,
    selectedUnifiedId,
    isCompareMode,
    currentImage,
  ]);

  const [visitSummaryResponse] = useVisitSummaries({
    visits: visitIdToImagesMap.map((item) => ({
      estimatedVisitId: item.estimatedVisitId,
      unifiedAppointmentId: item.unifiedAppointmentId,
    })),
  });

  const visitSummaryData = visitSummaryResponse?.data || [];

  const mixPanelData = formatMixPanelData(visitSummaryData);

  const imageIds = useMemo(() => {
    return Object.values(visitIdToImagesMap).map(
      (item) => item.images[0]?.imageId,
    );
  }, [visitIdToImagesMap]);
  const estimatedVisitIds = useMemo(() => {
    return (
      visitSummaryResponse?.data?.map((item) => item.estimatedVisitId) ?? []
    );
  }, [visitSummaryResponse?.data]);
  const unifiedAppointmentIds = useMemo(() => {
    return (
      visitSummaryResponse?.data?.map((item) => item.unifiedAppointmentId) ?? []
    );
  }, [visitSummaryResponse?.data]);

  return {
    visitSummaryResponse,
    mixPanelData: {
      ...mixPanelData,
      estimatedVisitIds,
      unifiedAppointmentIds,
      imageIds,
      imageId: imageIds.length === 1 ? imageIds[0] : undefined,
    },
    visitIdToImagesMap,
  };
};
